import { 
  Box, 
  useDisclosure, 
  useToast, 
  Flex, 
  Text, 
  HStack, 
  VStack, 
  Icon, 
  useBreakpointValue,
  Tooltip
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { MdVpnKey, MdCalendarToday, MdAccessTime, MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAppConfirm } from '../../components/common/AppConfirm/useAppConfirm';
import { AppLoading } from '../../components/common/AppLoading';
import { VideoGroupList, VideoGroupListItem } from '../../components/video/VideoGroupList';
import { useAuthContext } from '../../context/AuthContext';
import { useRoomReservationListQuery, useUpdateRoomReservationStatusMutation } from '../../hooks/coreApi/useRoomReservations';
import { RoomReservationResponse } from '../../openapi';
import { p2r } from '../../utils/font';
import { createQuolinkVideoRoomUrl } from '../../utils/room';
import EnterRoomReservationModal from './EnterRoomReservationModal';
import { EnterRoomButton, CancelButton, CreateReservationButton } from './VideoGroupButtons';

// メインコンポーネント
const MyReservedRooms: React.FC = () => {
  // 現在時刻から90分前の時刻を取得（日付フィルタリング用）
  const currentDate = dayjs().subtract(90, 'minute').startOf('minute').toISOString();
  
  // 現在時刻から90分前以降の予約を取得するクエリパラメータを設定
  const roomReservationListQuery = useRoomReservationListQuery({ 
    isMe: true, 
    withRoom: true,
    startDate: currentDate // 現在時刻から90分前以降の予約を取得
  });
  
  const reservations = roomReservationListQuery.data?.roomReservations ?? [];
  const { mutate: updateReservationStatus } = useUpdateRoomReservationStatusMutation();
  const { me } = useAuthContext();
  const navigate = useNavigate();
  const toast = useToast();
  const appConfirm = useAppConfirm();
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // 現在時刻を1分ごとに更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 1分ごとに更新
    
    return () => clearInterval(timer);
  }, []);
  
  // 予約時間内かどうかをチェックする関数
  const isWithinReservationTime = (startTime: string, endTime: string): boolean => {
    const now = currentTime;
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    // 10分前から入室可能にする
    const startWithBuffer = new Date(start);
    startWithBuffer.setMinutes(start.getMinutes() - 10);
    
    return now >= startWithBuffer && now <= end;
  };
  
  // 予約作成ページへ遷移
  const handleCreateReservation = () => {
    navigate('/video/group/reservation/create');
  };
  
  // モーダル状態管理
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedReservation, setSelectedReservation] = useState<RoomReservationResponse | null>(null);
  const [modalError, setModalError] = useState<string | undefined>(undefined);

  const handleCancel = async (reservationId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // 確認ダイアログを表示
    const confirmed = await appConfirm.open({
      title: '予約のキャンセル',
      body: '予約をキャンセルしてよろしいですか？',
      okText: 'キャンセルする',
      cancelText: '戻る'
    });
    
    if (confirmed) {
      updateReservationStatus({ 
        id: reservationId, 
        roomReservationUpdateStatusRequest: { status: 'cancelled' } 
      }, {
        onSuccess: () => {
          // APIが成功したらデータをリフェッチ
          roomReservationListQuery.refetch();
          
          toast({
            title: '予約をキャンセルしました',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        },
        onError: (error) => {
          toast({
            title: 'キャンセルに失敗しました',
            description: error instanceof Error ? error.message : '予期しないエラーが発生しました',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      });
    }
  };

  const handleEnterRoom = (reservation: RoomReservationResponse, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    // 予約時間外の場合は入室させない（二重チェック）
    if (!isWithinReservationTime(reservation.startTime, reservation.endTime)) {
      toast({
        title: '予約時間内のみ入室できます',
        description: `開始時間の10分前から入室可能です`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    if (!me) {
      toast({
        title: 'ログインしてください',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/login');
      return;
    };
    
    // パスワード付きの予約の場合
    if (reservation.password) {
      setSelectedReservation(reservation);
      setModalError(undefined);
      onOpen();
    } else {
      // パスワードなしの場合は直接入室
      window.location.href = createQuolinkVideoRoomUrl(reservation.roomId, me, dayjs(reservation.endTime));
    }
  };

  const handlePasswordSubmit = (inputPassword: string) => {
    if (!selectedReservation || !me) return;
    
    const correctPassword = selectedReservation.password;
    if (inputPassword === correctPassword) {
      onClose();
      window.location.href = createQuolinkVideoRoomUrl(selectedReservation.roomId, me);
    } else {
      setModalError('パスワードが一致しません');
      toast({
        title: 'パスワードが一致しません',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // 予約のボタン部分をレンダリングする関数
  const renderReservationButtons = (reservation: RoomReservationResponse, isCancelled: boolean, isTimeAvailable: boolean) => {
    if (isCancelled) {
      return (
        <Text color="red.500" fontWeight="bold" fontFamily="'BIZ UDPGothic', sans-serif">
          キャンセル済み
        </Text>
      );
    }

    const ButtonContainer = isMobile ? VStack : HStack;
    const spacing = isMobile ? 2 : 3;

    return (
      <ButtonContainer spacing={spacing} align={isMobile ? "stretch" : "center"}>
        <CancelButton
          onClick={(e: React.MouseEvent) => handleCancel(reservation.id ?? 0, e)}
          isMobile={isMobile}
        >
          取消
        </CancelButton>
        <Tooltip label={isTimeAvailable ? "" : "予約時間内のみ入室できます"} isDisabled={isTimeAvailable}>
          <EnterRoomButton
            onClick={(e: React.MouseEvent) => handleEnterRoom(reservation, e)}
            isMobile={isMobile}
            isDisabled={!isTimeAvailable}
          >
            入室する
          </EnterRoomButton>
        </Tooltip>
      </ButtonContainer>
    );
  };

  if (roomReservationListQuery.isLoading) {
    return <AppLoading />
  }

  return (
    <Box
      pt={isMobile ? '0px' : '24px'}
      pb={isMobile ? '0px' : '110px'}
      backgroundColor="grand.white"
      px={isMobile ? '16px' : '0px'}
    >
      <Flex mt={isMobile ? '0px' : '16px'} flexDirection="column" gap="24px">
        {reservations.length > 0 ? (
          <VideoGroupList>
            {reservations.map((reservation) => {
              const roomName = reservation?.room?.name || `グループ通話室${reservation?.roomId ?? ''}`;
              const isCancelled = reservation.status === 'cancelled';
              const isTimeAvailable = isWithinReservationTime(reservation.startTime, reservation.endTime);
              
              return (
                <VideoGroupListItem 
                  key={reservation.id}
                  title={roomName}
                  status="reserved"
                  buttons={renderReservationButtons(reservation, isCancelled, isTimeAvailable)}
                  withBorder
                  disabled={!isTimeAvailable || isCancelled}
                >
                  <Flex direction="column" gap="4px" maxW="100%">
                    {/* PC/タブレット: 2行レイアウト, スマホ: 縦並び */}
                    {isMobile ? (
                      // スマホ: 成り行きで段落ち
                      <>
                        <Flex alignItems="center" gap="8px" flexWrap="wrap">
                          <Icon as={MdEdit} boxSize="16px" color="#747678" />
                          <Text 
                            fontSize={p2r(16)} 
                            fontWeight="700" 
                            color="#1C1F21" 
                            lineHeight="140%"
                            fontFamily="'BIZ UDPGothic', sans-serif"
                          >
                            {reservation.title || '無題'}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" gap="8px" flexWrap="wrap">
                          <Icon as={MdCalendarToday} boxSize="16px" color="#747678" />
                          <Text fontSize={p2r(16)} color="#747678" fontFamily="'BIZ UDPGothic', sans-serif">
                            {dayjs(reservation.startTime).format('MM月DD日（ddd）')}
                          </Text>
                          <Icon as={MdAccessTime} boxSize="16px" color="#747678" />
                          <Text fontSize={p2r(16)} color="#747678" fontFamily="'BIZ UDPGothic', sans-serif">
                            {dayjs(reservation.startTime).format('HH:mm')}〜{dayjs(reservation.endTime).format('HH:mm')}
                          </Text>
                        </Flex>
                        {reservation.password && (
                          <Flex alignItems="center" gap="4px" flexWrap="wrap">
                            <Icon as={MdVpnKey} boxSize="16px" color="#747678" />
                            <Text fontSize={p2r(16)} color="#747678" fontFamily="'BIZ UDPGothic', sans-serif">
                              パスワード: {reservation.password}
                            </Text>
                          </Flex>
                        )}
                      </>
                    ) : (
                      // PC/タブレット: 2行レイアウト
                      <>
                        {/* 1段目：タイトル */}
                        <Flex alignItems="center" gap="8px" flexWrap="wrap">
                          <Icon as={MdEdit} boxSize="16px" color="#747678" />
                          <Text 
                            fontSize={p2r(16)} 
                            fontWeight="700" 
                            color="#1C1F21" 
                            lineHeight="140%"
                            fontFamily="'BIZ UDPGothic', sans-serif"
                          >
                            {reservation.title || '無題'}
                          </Text>
                        </Flex>
                        {/* 2段目：その他の情報 */}
                        <Flex alignItems="center" gap="8px" flexWrap="wrap">
                          <Icon as={MdCalendarToday} boxSize="16px" color="#747678" />
                          <Text fontSize={p2r(16)} color="#747678" fontFamily="'BIZ UDPGothic', sans-serif">
                            {dayjs(reservation.startTime).format('MM月DD日（ddd）')}
                          </Text>
                          <Icon as={MdAccessTime} boxSize="16px" color="#747678" />
                          <Text fontSize={p2r(16)} color="#747678" fontFamily="'BIZ UDPGothic', sans-serif">
                            {dayjs(reservation.startTime).format('HH:mm')}〜{dayjs(reservation.endTime).format('HH:mm')}
                          </Text>
                          {reservation.password && (
                            <>
                              <Icon as={MdVpnKey} boxSize="16px" color="#747678" />
                              <Text fontSize={p2r(16)} color="#747678" fontFamily="'BIZ UDPGothic', sans-serif">
                                パスワード: {reservation.password}
                              </Text>
                            </>
                          )}
                        </Flex>
                      </>
                    )}
                  </Flex>
                </VideoGroupListItem>
              );
            })}
          </VideoGroupList>
        ) : (
          <Flex 
            direction="column" 
            alignItems="center" 
            justifyContent="center"
            bg="transparent"
            p={8}
            minHeight="150px"
          >
            <Text fontSize="lg" color="gray.500" mb={4} fontFamily="'BIZ UDPGothic', sans-serif">予約はありません</Text>
            <CreateReservationButton onClick={handleCreateReservation}>
              予約作成
            </CreateReservationButton>
          </Flex>
        )}
      </Flex>
      
      {/* パスワード入力モーダル */}
      <EnterRoomReservationModal
        isOpen={isOpen}
        onClose={onClose}
        onEnter={handlePasswordSubmit}
        error={modalError}
      />
    </Box>
  );
};

export default MyReservedRooms; 